import React, { useState } from 'react';
import './Steps.css';

const Languages = ({ formData, setFormData, language = 'fr' }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [currentInputIndex, setCurrentInputIndex] = useState(null);

  // Liste des langues communes pour les suggestions
  const commonLanguages = [
    'Arabe', 'Français', 'Anglais', 'Espagnol', 'Allemand', 'Italien', 
    'Chinois', 'Japonais', 'Russe', 'Portugais', 'Néerlandais', 'Turc',
    'Hindi', 'Coréen', 'Suédois', 'Norvégien', 'Danois', 'Finnois',
    'Polonais', 'Grec', 'Hébreu', 'Thaï', 'Vietnamien', 'Persan'
  ];

  const t = {
    title: language === 'fr' ? 'Langues' : 'Languages',
    itemTitle: (i) => language === 'fr' ? `Langue ${i + 1}` : `Language ${i + 1}`,
    langLabel: language === 'fr' ? 'Langue *' : 'Language *',
    langPlaceholder: language === 'fr' ? 'Arabe' : 'Arabic',
    levelLabel: language === 'fr' ? 'Niveau *' : 'Proficiency *',
    selectPlaceholder: language === 'fr' ? 'Sélectionnez un niveau' : 'Select level',
    levels: {
      native: language === 'fr' ? 'Langue maternelle' : 'Native',
      fluent: language === 'fr' ? 'Courant' : 'Fluent',
      advanced: language === 'fr' ? 'Niveau avancé ' : 'Advanced ',
      intermediate: language === 'fr' ? 'Intermédiaire ' : 'Intermediate ',
      beginner: language === 'fr' ? 'Débutant ' : 'Beginner '
    },
    removeBtn: language === 'fr' ? 'Supprimer' : 'Remove',
    addBtn: language === 'fr' ? '+ Ajouter une langue' : '+ Add Language'
  };

  const handleLanguageInput = (index, value) => {
    // Mettre à jour la valeur du champ
    handleLanguageChange(index, 'language', value);
    
    // Générer les suggestions
    if (value.length > 0) {
      const filtered = commonLanguages.filter(lang =>
        lang.toLowerCase().includes(value.toLowerCase())
      );
      setSuggestions(filtered);
      setActiveIndex(-1);
      setCurrentInputIndex(index);
    } else {
      setSuggestions([]);
      setCurrentInputIndex(null);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    if (currentInputIndex !== null) {
      handleLanguageChange(currentInputIndex, 'language', suggestion);
      setSuggestions([]);
      setActiveIndex(-1);
      setCurrentInputIndex(null);
    }
  };

  const handleKeyDown = (e) => {
    if (suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(prev => (prev + 1) % suggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        handleSuggestionClick(suggestions[activeIndex]);
      } else if (e.key === 'Escape') {
        setSuggestions([]);
        setActiveIndex(-1);
        setCurrentInputIndex(null);
      }
    }
  };

  const handleLanguageChange = (index, field, value) => {
    const newLanguages = [...formData.languages];
    newLanguages[index][field] = value;
    setFormData(prev => ({ ...prev, languages: newLanguages }));
  };

  const addLanguage = () => {
    setFormData(prev => ({
      ...prev,
      languages: [...prev.languages, { language: '', level: '' }]
    }));
    setSuggestions([]);
    setActiveIndex(-1);
    setCurrentInputIndex(null);
  };

  const removeLanguage = (index) => {
    const newLanguages = formData.languages.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, languages: newLanguages }));
    setSuggestions([]);
    setCurrentInputIndex(null);
  };

  return (
    <div className="step-content">
      <h2>{t.title}</h2>
      
      {formData.languages.map((lang, index) => (
        <div key={index} className="form-item">
          <div className="item-header">
            <h3>{t.itemTitle(index)}</h3>
            {formData.languages.length > 1 && (
              <button type="button" className="remove-btn" onClick={() => removeLanguage(index)}>
                {t.removeBtn}
              </button>
            )}
          </div>

          <div className="form-grid">
            <div className="input-group">
              <label>{t.langLabel}</label>
              <div className="suggestions-container">
                <input
                  type="text"
                  value={lang.language}
                  onChange={(e) => handleLanguageInput(index, e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={() => setCurrentInputIndex(index)}
                  onBlur={() => setTimeout(() => {
                    setSuggestions([]);
                    setCurrentInputIndex(null);
                  }, 200)}
                  placeholder={t.langPlaceholder}
                  required
                />
                {suggestions.length > 0 && currentInputIndex === index && (
                  <div className="suggestions-list">
                    {suggestions.map((suggestion, i) => (
                      <div
                        key={suggestion}
                        className={`suggestion-item ${i === activeIndex ? 'active' : ''}`}
                        onMouseDown={(e) => {
                          e.preventDefault(); // Empêcher le blur de l'input
                          handleSuggestionClick(suggestion);
                        }}
                        onMouseEnter={() => setActiveIndex(i)}
                      >
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="input-group">
              <label>{t.levelLabel}</label>
              <select
                value={lang.level}
                onChange={(e) => handleLanguageChange(index, 'level', e.target.value)}
                required
              >
                <option value="">{t.selectPlaceholder}</option>
                <option value={t.levels.native}>{t.levels.native}</option>
                <option value={t.levels.fluent}>{t.levels.fluent}</option>
                <option value={t.levels.advanced}>{t.levels.advanced}</option>
                <option value={t.levels.intermediate}>{t.levels.intermediate}</option>
                <option value={t.levels.beginner}>{t.levels.beginner}</option>
              </select>
            </div>
          </div>
        </div>
      ))}

      <button type="button" className="add-btn" onClick={addLanguage}>
        {t.addBtn}
      </button>
    </div>
  );
};

export default Languages;